/**
 * Offline storage model (Dexie DB 'kgr-offline' + Cache API).
 *
 *  - packs: what the user asked to keep ("Vista fyrir ónettengt"): one song, one collection or "allt mitt".
 *  - items: one row per media URL stored in the 'kgr-content-v1' cache, shared by the packs listed in packIds.
 *  - meta:  last manifest, device tier, recent-songs LRU bookkeeping, cached avatar URLs.
 *
 * The manifest (GET /api/offline/manifest) is authoritative: packs are re-planned against every new
 * manifest and anything not in it is deleted (see diff.ts).
 */
import type { MediaPart } from '../../shared/dto/library';

export const CONTENT_CACHE = 'kgr-content-v1';
export const RECENT_CACHE = 'kgr-recent-v1';
export const OFFLINE_DB_NAME = 'kgr-offline';

export type PackKind = 'song' | 'collection' | 'all';

/**
 * Stored pack status:
 *  - pending:  not every planned file is stored yet (new pack, or a download that did not finish)
 *  - complete: every planned file is stored
 *  - update:   was complete, then the manifest changed (new revision / new file): re-downloads run automatically
 *  - error:    the download gave up (see `error`); retried on the next online start or by the user
 */
export type PackStatus = 'pending' | 'complete' | 'update' | 'error';

export interface PackRow {
  /** 'song:<id>' | 'collection:<id>' | 'all' */
  id: string;
  kind: PackKind;
  /** Song / collection id; null for 'all'. */
  refId: string | null;
  /** Song title / collection name when saved ('' for "allt mitt", which is labelled by the UI). */
  title: string;
  createdAt: number;
  status: PackStatus;
  /** Planned bytes (all files of the pack). */
  bytes: number;
  /** Error code when status = 'error'. */
  error: string | null;
  /** Editors only: also every other voice's files ("allar raddir"). */
  allSections: boolean;
}

export interface ItemRow {
  /** Same-origin media URL, the Cache API key: /api/media/<fileId>/<rev>/<part> */
  url: string;
  fileId: string;
  songId: string;
  rev: number;
  part: MediaPart;
  size: number;
  /** Unquoted ETag (as listed in the manifest). */
  etag: string;
  mime: string;
  packIds: string[];
  cachedAt: number;
}

/** A file part a pack needs (from planning). */
export interface PlannedItem {
  url: string;
  fileId: string;
  songId: string;
  rev: number;
  part: MediaPart;
  size: number;
  etag: string;
  mime: string;
}

/** Recent-songs LRU entry: the sheet bundles of one opened song stored in 'kgr-recent-v1'. */
export interface RecentEntry {
  /** Song id, or 'file:<fileId>' when the song is unknown. */
  key: string;
  urls: { url: string; size: number }[];
  lastUsed: number;
}

export const RECENT_LIMITS = { maxSongs: 10, maxBytes: 300 * 1024 * 1024 } as const;

export function packId(kind: PackKind, refId: string | null): string {
  return kind === 'all' ? 'all' : `${kind}:${refId}`;
}
